/**
 * Publication Classifier
 * Detects whether a DJEN publication should become an Event or Task
 * Extracts relevant information (date, time, location, etc.)
 */

import type { EventType } from '../types/calendar'

export interface PublicationClassification {
  type: 'event' | 'task'
  confidence: 'high' | 'medium' | 'low'
  suggestedEventType?: EventType
  suggestedTaskCategory?: 'PRAZO_PROCESSUAL' | 'ACAO_INTERNA' | 'DOCUMENTO'
  extractedData: {
    date?: string         // ISO date
    startTime?: string    // HH:mm
    endTime?: string      // HH:mm
    location?: string
    hearingType?: string
    courtName?: string
    deadline?: number     // dias de prazo
  }
  reasoning: string[]
}

/**
 * Classifica uma publicação e extrai informações relevantes
 */
export function classifyPublication(texto: string): PublicationClassification {
  if (!texto) {
    return {
      type: 'task',
      confidence: 'low',
      suggestedTaskCategory: 'PRAZO_PROCESSUAL',
      extractedData: {},
      reasoning: ['Texto da publicação não disponível']
    }
  }
  const textoLower = texto.toLowerCase()
  const reasoning: string[] = []

  // ===== DETECÇÃO DE EVENTO =====

  // Palavras-chave que indicam EVENTO (comparecimento)
  const eventKeywords = {
    audiencia: ['audiência', 'audiencia'],
    sessao: ['sessão', 'sessao', 'julgamento'],
    reuniao: ['reunião', 'reuniao', 'conciliação', 'conciliacao', 'mediação', 'mediacao'],
    horario: [
      'às ', 'as ',
      ' h', 'h:', ':00', ':30', ':15', ':45',
      'hora', 'horas'
    ],
    local: [
      'sala ', 'fórum', 'forum', 'tribunal',
      'videoconferência', 'videoconferencia',
      'presencial', 'virtual'
    ]
  }

  // Palavras-chave que indicam TAREFA (prazo/deadline)
  const taskKeywords = {
    prazo: [
      'prazo para', 'prazo de', 'no prazo',
      'intimação para', 'intimacao para',
      'intima-se para', 'intima-se',
      'fica intimado para', 'ficam intimados para'
    ],
    acoes: [
      'apresentar', 'protocolar', 'juntar',
      'contestação', 'contestacao',
      'recurso', 'agravo',
      'manifestação', 'manifestacao',
      'alegações', 'alegacoes',
      'impugnação', 'impugnacao',
      'réplica', 'replica'
    ],
    prazoDias: [
      'dias', 'dias úteis', 'dias uteis',
      '5 dias', '10 dias', '15 dias', '20 dias', '30 dias'
    ]
  }

  // Pontuar palavras-chave
  let eventScore = 0
  let taskScore = 0

  // Score para eventos
  if (eventKeywords.audiencia.some(kw => textoLower.includes(kw))) {
    eventScore += 3
    reasoning.push('Menciona "audiência"')
  }

  if (eventKeywords.sessao.some(kw => textoLower.includes(kw))) {
    eventScore += 2
    reasoning.push('Menciona "sessão/julgamento"')
  }

  if (eventKeywords.reuniao.some(kw => textoLower.includes(kw))) {
    eventScore += 2
    reasoning.push('Menciona "reunião/conciliação"')
  }

  if (eventKeywords.horario.some(kw => textoLower.includes(kw))) {
    eventScore += 3
    reasoning.push('Contém horário específico')
  }

  if (eventKeywords.local.some(kw => textoLower.includes(kw))) {
    eventScore += 2
    reasoning.push('Menciona local/sala')
  }

  // Score para tarefas
  if (taskKeywords.prazo.some(kw => textoLower.includes(kw))) {
    taskScore += 3
    reasoning.push('Menciona "prazo"')
  }

  if (taskKeywords.acoes.some(kw => textoLower.includes(kw))) {
    taskScore += 2
    reasoning.push('Menciona ação/entrega')
  }

  if (taskKeywords.prazoDias.some(kw => textoLower.includes(kw))) {
    taskScore += 2
    reasoning.push('Menciona prazo em dias')
  }

  // ===== EXTRAÇÃO DE DADOS =====

  const extractedData: PublicationClassification['extractedData'] = {}

  // Extrair data (dd/mm/yyyy)
  const dateMatch = texto.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/g)
  if (dateMatch && dateMatch.length > 0) {
    const firstDate = dateMatch[0]
    const [day, month, year] = firstDate.split(/[/-]/)
    const fullYear = year.length === 2 ? `20${year}` : year
    extractedData.date = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    reasoning.push(`Data extraída: ${firstDate}`)
  }

  // Extrair horário (HH:mm, HH:00, HHh, HH horas)
  const timeMatch = texto.match(/(\d{1,2})[h:](\d{2})|(\d{1,2})h|(\d{1,2})\s*horas/i)
  if (timeMatch) {
    const hour = timeMatch[1] || timeMatch[3] || timeMatch[4]
    const minute = timeMatch[2] || '00'
    extractedData.startTime = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`
    reasoning.push(`Horário extraído: ${extractedData.startTime}`)
  }

  // Extrair local/sala
  const locationMatch = texto.match(/sala\s+(\d+|[a-z\d]+)/i)
  if (locationMatch) {
    extractedData.location = `Sala ${locationMatch[1]}`
    reasoning.push(`Local extraído: ${extractedData.location}`)
  }

  // Extrair tipo de audiência
  if (textoLower.includes('audiência')) {
    if (textoLower.includes('instrução')) extractedData.hearingType = 'INSTRUÇÃO'
    else if (textoLower.includes('conciliação')) extractedData.hearingType = 'CONCILIAÇÃO'
    else if (textoLower.includes('inicial')) extractedData.hearingType = 'INICIAL'
    else if (textoLower.includes('julgamento')) extractedData.hearingType = 'JULGAMENTO'
  }

  // Extrair tribunal/fórum
  const courtMatch = texto.match(/(tribunal|fórum|forum)\s+([\w\s]+?)(?:\s*-|\s*sala|\.|$)/i)
  if (courtMatch) {
    extractedData.courtName = courtMatch[0].trim()
  }

  // Extrair prazo em dias
  const deadlineMatch = texto.match(/(\d+)\s*dias?\s*(?:úteis|uteis)?/i)
  if (deadlineMatch) {
    extractedData.deadline = parseInt(deadlineMatch[1])
    reasoning.push(`Prazo: ${extractedData.deadline} dias`)
  }

  // ===== DECISÃO FINAL =====

  let type: 'event' | 'task'
  let confidence: 'high' | 'medium' | 'low'
  let suggestedEventType: EventType | undefined
  let suggestedTaskCategory: 'PRAZO_PROCESSUAL' | 'ACAO_INTERNA' | 'DOCUMENTO' | undefined

  if (eventScore > taskScore && eventScore >= 3) {
    type = 'event'
    confidence = eventScore >= 5 ? 'high' : 'medium'

    // Determinar tipo de evento
    if (textoLower.includes('audiência') || textoLower.includes('audiencia')) {
      suggestedEventType = 'AUDIENCIA'
    } else if (textoLower.includes('reunião') || textoLower.includes('reuniao') ||
               textoLower.includes('conciliação') || textoLower.includes('conciliacao')) {
      suggestedEventType = 'REUNIAO'
    } else {
      suggestedEventType = 'COMPROMISSO'
    }

    reasoning.push(`→ Classificado como EVENTO (${suggestedEventType})`)
  } else if (taskScore > eventScore && taskScore >= 2) {
    type = 'task'
    confidence = taskScore >= 4 ? 'high' : 'medium'

    // Determinar categoria da tarefa
    if (textoLower.includes('prazo')) {
      suggestedTaskCategory = 'PRAZO_PROCESSUAL'
    } else if (textoLower.includes('documento') || textoLower.includes('juntar')) {
      suggestedTaskCategory = 'DOCUMENTO'
    } else {
      suggestedTaskCategory = 'ACAO_INTERNA'
    }

    reasoning.push(`→ Classificado como TAREFA (${suggestedTaskCategory})`)
  } else {
    // Empate ou pontuação baixa - default para tarefa
    type = 'task'
    confidence = 'low'
    suggestedTaskCategory = 'PRAZO_PROCESSUAL'
    reasoning.push('→ Classificação incerta - defaulting para TAREFA')
  }

  return {
    type,
    confidence,
    suggestedEventType,
    suggestedTaskCategory,
    extractedData,
    reasoning
  }
}

/**
 * Calcula data de vencimento baseada em prazo de dias
 */
export function calculateDeadlineDate(publicationDate: string, days: number): string {
  const date = new Date(publicationDate)

  // Adicionar dias úteis (simplificado - não considera feriados)
  let addedDays = 0
  while (addedDays < days) {
    date.setDate(date.getDate() + 1)
    const dayOfWeek = date.getDay()
    // Pular finais de semana
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      addedDays++
    }
  }

  return date.toISOString().split('T')[0]
}

/**
 * Formata o raciocínio da classificação para exibir ao usuário
 */
export function formatClassificationReasoning(classification: PublicationClassification): string {
  return classification.reasoning.join('\n• ')
}
